/**
 * Opportunity Matrix — Risk (x) × Effort (y), bubble size = ROI score.
 *
 * Quadrant convention (top-left is most attractive):
 *   Low risk · Low effort  → "Quick wins"
 *   Low risk · High effort → "Strategic bets"
 *   High risk · Low effort → "Watch carefully"
 *   High risk · High effort → "Reconsider"
 */
import React, { useState } from "react";
import { cn } from "@/lib/utils";

const W = 760;
const H = 460;
const PAD_X = 56;
const PAD_Y = 36;
const INNER_W = W - PAD_X * 2;
const INNER_H = H - PAD_Y * 2;

const bubbleColor = (risk) =>
  risk === "low" ? "#0E8762" : risk === "med" ? "#B5651D" : "#A22F1F";

const bubbleRadius = (roiScore) => 6 + (roiScore / 100) * 18;

// Bucketed risk + effort scores cause bubbles to pile up at the same
// coordinates. We apply a deterministic ±jitter derived from the
// opportunity id so each bubble lands in a slightly different spot and
// the cluster reads as a swarm instead of a stack. Stable across renders.
function jitterFor(id, range = 26) {
  let h = 0;
  for (let i = 0; i < id.length; i += 1) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  const dx = ((h % 1000) / 1000) * range - range / 2;
  const dy = (((h >> 10) % 1000) / 1000) * range - range / 2;
  return { dx, dy };
}

export function OpportunityMatrix({ items, onSelect }) {
  const [hoverId, setHoverId] = useState(null);

  return (
    <div
      className="surface-card shadow-e1 p-4 relative overflow-auto"
      data-testid="radar-matrix"
    >
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto block" role="img" aria-label="Opportunity matrix">
        {/* Axes */}
        <line x1={PAD_X} y1={H - PAD_Y} x2={W - PAD_X} y2={H - PAD_Y} stroke="#CBD5E1" strokeWidth="1" />
        <line x1={PAD_X} y1={PAD_Y} x2={PAD_X} y2={H - PAD_Y} stroke="#CBD5E1" strokeWidth="1" />

        {/* Quadrant guides at 50/50 */}
        <line
          x1={PAD_X + INNER_W / 2}
          y1={PAD_Y}
          x2={PAD_X + INNER_W / 2}
          y2={H - PAD_Y}
          stroke="#E2E8F0"
          strokeDasharray="2 4"
        />
        <line
          x1={PAD_X}
          y1={PAD_Y + INNER_H / 2}
          x2={W - PAD_X}
          y2={PAD_Y + INNER_H / 2}
          stroke="#E2E8F0"
          strokeDasharray="2 4"
        />

        {/* Quadrant labels */}
        <QuadrantLabel x={PAD_X + 8} y={PAD_Y + 16}>Quick wins</QuadrantLabel>
        <QuadrantLabel x={PAD_X + INNER_W / 2 + 8} y={PAD_Y + 16}>Watch carefully</QuadrantLabel>
        <QuadrantLabel x={PAD_X + 8} y={H - PAD_Y - 8}>Strategic bets</QuadrantLabel>
        <QuadrantLabel x={PAD_X + INNER_W / 2 + 8} y={H - PAD_Y - 8}>Reconsider</QuadrantLabel>

        {/* Axis ticks */}
        {[0, 25, 50, 75, 100].map((t) => (
          <g key={`x-${t}`}>
            <text
              x={PAD_X + (t / 100) * INNER_W}
              y={H - PAD_Y + 16}
              textAnchor="middle"
              fontSize="10"
              fill="#475569"
              fontFamily="JetBrains Mono, monospace"
            >
              {t}
            </text>
          </g>
        ))}
        {[0, 25, 50, 75, 100].map((t) => (
          <g key={`y-${t}`}>
            <text
              x={PAD_X - 8}
              y={H - PAD_Y - (t / 100) * INNER_H + 3}
              textAnchor="end"
              fontSize="10"
              fill="#475569"
              fontFamily="JetBrains Mono, monospace"
            >
              {t}
            </text>
          </g>
        ))}

        {/* Axis titles */}
        <text
          x={PAD_X + INNER_W / 2}
          y={H - 6}
          textAnchor="middle"
          fontSize="11"
          fill="#0F172A"
          fontWeight="600"
        >
          Risk score →
        </text>
        <text
          x={14}
          y={PAD_Y + INNER_H / 2}
          textAnchor="middle"
          fontSize="11"
          fill="#0F172A"
          fontWeight="600"
          transform={`rotate(-90 14 ${PAD_Y + INNER_H / 2})`}
        >
          ← Effort score
        </text>

        {/* Bubbles */}
        {items.map((o) => {
          const { dx, dy } = jitterFor(o.id);
          const cx = PAD_X + (o.riskScore / 100) * INNER_W + dx;
          const cy = H - PAD_Y - (o.effortScore / 100) * INNER_H + dy;
          const r = bubbleRadius(o.roiScore);
          const fill = bubbleColor(o.risk);
          const isHover = hoverId === o.id;
          // Bring hovered bubble to the front via SVG paint order.
          return (
            <g
              key={o.id}
              data-testid={`radar-matrix-bubble-${o.id}`}
              className="cursor-pointer"
              onMouseEnter={() => setHoverId(o.id)}
              onMouseLeave={() => setHoverId(null)}
              onClick={() => onSelect(o)}
              style={isHover ? { filter: "drop-shadow(0 1px 2px rgba(15, 23, 42, 0.35))" } : undefined}
            >
              <circle
                cx={cx}
                cy={cy}
                r={r + 2}
                fill={fill}
                fillOpacity={isHover ? 0.22 : 0.08}
              />
              <circle cx={cx} cy={cy} r={r} fill={fill} fillOpacity={isHover ? 0.95 : 0.7} stroke="#fff" strokeWidth="1.5" />
              {isHover && (
                <g>
                  {/* Label background pill so text stays readable on top of other bubbles */}
                  <rect
                    x={cx + r + 4}
                    y={cy - 9}
                    width={Math.min(o.title.length, 36) * 5.8 + 12}
                    height={18}
                    rx={4}
                    fill="#0F172A"
                    fillOpacity="0.92"
                  />
                  <text
                    x={cx + r + 10}
                    y={cy + 3}
                    fontSize="10"
                    fill="#FFFFFF"
                    fontWeight="600"
                  >
                    {o.title.length > 36 ? `${o.title.slice(0, 36)}…` : o.title}
                  </text>
                </g>
              )}
            </g>
          );
        })}
      </svg>

      <Legend />
    </div>
  );
}

function QuadrantLabel({ x, y, children }) {
  return (
    <text
      x={x}
      y={y}
      fontSize="9"
      fill="#94A3B8"
      fontWeight="700"
      letterSpacing="0.08em"
      style={{ textTransform: "uppercase" }}
    >
      {children}
    </text>
  );
}

function Legend() {
  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-meta text-ink-secondary">
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: "#0E8762" }} /> Low risk
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: "#B5651D" }} /> Medium risk
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: "#A22F1F" }} /> High risk
      </span>
      <span className="ml-auto text-ink-disabled">
        Bubble size = ROI score · Hover for title · Click a bubble to open detail
      </span>
    </div>
  );
}
